import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Wand2, Sparkles, CreditCard, Globe2, Users, ShieldCheck, Mail, MessagesSquare, DollarSign, Tags } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { useRole } from "@/contexts/RoleContext";
import LogoutButton from "@/components/LogoutButton";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard", nav: "navHome" },
  { to: "/wizard", icon: Wand2, key: "wizard", nav: "navSetup" },
  { to: "/compliance", icon: ShieldCheck, key: "compliance", nav: "navDocs" },
  { to: "/chat", icon: Sparkles, key: "chat", nav: "navAI" },
  { to: "/community", icon: MessagesSquare, key: "community", nav: "navCommunity" },
  { to: "/pricing", icon: CreditCard, key: "pricing", nav: "navPlans" },
];

const adminItems = [
  { to: "/admin", icon: Users, key: "admin", label: "Customers", exact: true },
  { to: "/admin/revenue", icon: DollarSign, key: "revenue", label: "Revenue" },
  { to: "/admin/plans", icon: Tags, key: "plans", label: "Plans" },
  { to: "/compliance", icon: ShieldCheck, key: "compliance", nav: "navDocs" },
  { to: "/admin/messages", icon: Mail, key: "messages", label: "Messages" },
  { to: "/community", icon: MessagesSquare, key: "community", nav: "navCommunity" },
  { to: "/chat", icon: Sparkles, key: "chat", nav: "navAI" },
];

export default function Shell({ children }) {
  const { t } = useLang();
  const loc = useLocation();
  const navigate = useNavigate();
  const { actingAsAdmin } = useRole();
  const items = actingAsAdmin ? adminItems : navItems;
  const label = (n) => (n.nav ? t(n.nav) : n.label);
  const isActive = (to) => (to === "/admin" ? loc.pathname === "/admin" : loc.pathname.startsWith(to));
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    const set = () => document.documentElement.style.setProperty("--shell-header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header ref={headerRef} className="border-b border-[#262626] sticky top-0 z-30 bg-background">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4">
          <Link to={actingAsAdmin ? "/admin" : "/dashboard"} className="inline-flex min-w-0 lg:shrink-0" data-testid="shell-brand">
            <Logo />
          </Link>
          <div className="md:hidden shrink-0 flex items-center gap-1">
            <button onClick={() => navigate("/")} data-testid="mnav-language" aria-label="Language" className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-white">
              <Globe2 size={18} />
            </button>
            <LogoutButton testid="mobile-logout-button" compact />
          </div>
          <nav className="hidden md:flex flex-wrap xl:flex-nowrap min-w-0 items-center justify-end gap-1 gap-y-2">
            {items.map((n) => {
              const active = isActive(n.to);
              if (n.key === "pricing") {
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid="nav-pricing"
                    className={`mx-1 px-4 py-2.5 flex items-center gap-2 font-mono-label text-xs text-white whitespace-nowrap bg-[#16A34A] hover:bg-[#15803D] border border-[#16A34A] transition-colors ${active ? "ring-2 ring-white/60" : ""}`}
                  >
                    <n.icon size={16} /> <span>{label(n)}</span>
                  </Link>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.key}`}
                  title={label(n)}
                  className={`px-3 py-2 flex items-center gap-2 font-mono-label text-[10px] whitespace-nowrap transition-colors ${active ? "bg-[#141414] text-white border border-[#262626]" : "text-muted-foreground hover:text-white"}`}
                >
                  <n.icon size={14} /> <span className="hidden xl:inline">{label(n)}</span>
                </Link>
              );
            })}
            <button data-testid="nav-change-lang" onClick={() => navigate("/")} className="px-3 py-2 flex items-center gap-2 font-mono-label text-[10px] text-muted-foreground hover:text-white transition-colors">
              <Globe2 size={14} />
            </button>
            <div className="ml-2"><UserButton /></div>
            <div className="ml-2"><LogoutButton /></div>
          </nav>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 pb-[calc(9.5rem+env(safe-area-inset-bottom))] md:pb-28">{children}</main>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-[#262626] grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`, paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {items.map((n) => {
          const green = n.key === "pricing";
          const active = isActive(n.to);
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mnav-${n.key}`}
              className={`flex flex-col items-center justify-center gap-1 min-h-[60px] px-0.5 text-[10px] tracking-tight font-semibold transition-colors ${green ? "bg-[#16A34A] text-white" : active ? "text-[#FFD600]" : "text-muted-foreground"}`}
            >
              <n.icon size={20} />
              <span className="truncate max-w-full">{label(n)}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
